/** Whether the signed-in user holds a Field-only licence in the active
 *  workspace. Field members work on their assigned sites, so commercial Home
 *  surfaces (pipeline, profit and loss, outstanding, tender and payment dates)
 *  are hidden from them. The server already withholds the figures; this only
 *  stops empty commercial cards being drawn. Unknown until the check answers,
 *  and a failed check reads as not Field-only so Full members never lose Home.
 *  Platform staff can hold workspace memberships but keep the cross-workspace
 *  dashboard, so they are never classed as Field members. */

import { useEffect, useState } from 'react';
import { useAuth } from '../platform/auth/AuthContext';
import { SoftwareBillingService } from '../services/SoftwareBillingService';
import { isInternalRole } from '../utils/permissions';

const cache = new Map<string, boolean>();

export function useFieldOnlyMember(): boolean {
  const auth = useAuth();
  const workspaceId = auth.activeWorkspaceId ?? null;
  const internal = isInternalRole(auth.user?.role);
  const key = !internal && workspaceId && auth.user?.id ? `${auth.user.id}:${workspaceId}` : null;
  // The answer is stored with the key it belongs to, so a workspace switch
  // never shows the previous workspace's layout while the new check runs.
  const [answer, setAnswer] = useState<{ key: string; fieldOnly: boolean } | null>(null);

  useEffect(() => {
    if (!key || !workspaceId || cache.has(key)) return;
    const controller = new AbortController();
    SoftwareBillingService.access(workspaceId, controller.signal)
      .then(({ licenceType }) => {
        cache.set(key, licenceType === 'field');
        if (!controller.signal.aborted) setAnswer({ key, fieldOnly: licenceType === 'field' });
      })
      .catch(() => { /* not Field-only: the server still masks every figure */ });
    return () => controller.abort();
  }, [key, workspaceId]);

  if (!key) return false;
  return cache.get(key) ?? (answer?.key === key ? answer.fieldOnly : false);
}
